// Foraging: mushrooms at forest floors, berry bushes on meadow edges, wild
// apple trees near open land — deterministic (seeded per 250 m cell), sparse
// on purpose (finding them should take LOOKING), picked state per session.
// Also renders dropped ground items and feeds the interact prompt.
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Vector3, Matrix, Quaternion, Color3 } from '@babylonjs/core/Maths/math';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { ITEMS } from '../game/items';
// one guard for "remove this entry from that list", shared with loot.js
import { findRef, takeRef } from './loot-core.mjs';

const CELL = 250;
// species → visual + biome pools. Forest biomes: 4 birch, 5 pine, 6 mixed;
// 2/3 = open/meadow-ish. Weights bias toward the common edibles.
const MUSHROOMS = [
  { id: 'mush_porcini', cap: '#8a5a2b', stem: '#e8dcc8', capR: 0.16, h: 0.14, biomes: [4, 5, 6], w: 3 },
  { id: 'mush_chanterelle', cap: '#e8a832', stem: '#e0b060', capR: 0.10, h: 0.09, biomes: [5, 6], w: 3 },
  { id: 'mush_birch_bolete', cap: '#7a5138', stem: '#d8d0c0', capR: 0.13, h: 0.13, biomes: [4, 6], w: 3 },
  { id: 'mush_puffball', cap: '#e8e4d8', stem: '#e8e4d8', capR: 0.14, h: 0.10, biomes: [2, 4, 6], w: 2 },
  { id: 'mush_morel', cap: '#5a4530', stem: '#e0d8c0', capR: 0.09, h: 0.15, biomes: [4, 6], w: 2 },
  { id: 'mush_fly_agaric', cap: '#c83a28', stem: '#f0ece0', capR: 0.15, h: 0.16, biomes: [4, 5, 6], w: 2 },
  { id: 'mush_false_morel', cap: '#6b3a28', stem: '#d8ccb8', capR: 0.12, h: 0.12, biomes: [5, 6], w: 1 },
  { id: 'mush_death_cap', cap: '#c8c8a8', stem: '#e8e8d8', capR: 0.13, h: 0.15, biomes: [4, 6], w: 1 },
];
const BUSHES = [
  { id: 'berry_blueberry', berry: '#3a4a8a', leaf: '#2e5230', size: 0.55, biomes: [4, 6], w: 3 },
  { id: 'berry_raspberry', berry: '#c83a58', leaf: '#3a6234', size: 0.8, biomes: [2, 3, 6], w: 3 },
  { id: 'berry_lingonberry', berry: '#c02828', leaf: '#2a4a2c', size: 0.4, biomes: [4, 5], w: 2 },
  { id: 'berry_elderberry', berry: '#241a30', leaf: '#3e6238', size: 1.3, biomes: [2, 3], w: 2 },
  { id: 'berry_rosehip', berry: '#d04020', leaf: '#3a5a34', size: 1.0, biomes: [2, 3], w: 2 },
  { id: 'berry_rowan', berry: '#e06020', leaf: '#3a5c30', size: 1.6, biomes: [2, 4, 6], w: 2 },
];

// ground drops are identified by uid, not by `key` like forage spots are
const uidOf = (g) => g?.uid;

function hash2(x, z, salt) {
  let h = (x * 374761393 + z * 668265263 + salt * 971) | 0;
  h = (h ^ (h >> 13)) * 1274126177;
  return (((h ^ (h >> 16)) >>> 0) % 100000) / 100000;
}

export class Forage {
  constructor(scene, terrain, veg) {
    this.scene = scene;
    this.terrain = terrain;
    this.veg = veg;
    this.picked = new Set();      // spot keys harvested this session
    this.ground = [];             // dropped items: {uid, id, qty, x, y, z}
    this.groundUid = 1;
    this.lastBuild = new Vector3(1e9, 0, 1e9);
    this.spots = [];              // nearby unharvested spots
    this.templates = new Map();   // tpl name → [meshes] sharing thin instances
    this._initMeshes();
  }

  _mat(name, hex) {
    const m = new StandardMaterial(name, this.scene);
    m.diffuseColor = Color3.FromHexString(hex);
    m.emissiveColor = m.diffuseColor.scale(0.18); // lift shadows a touch
    m.specularColor = Color3.Black();
    return m;
  }

  _tpl(name, meshes) {
    for (const m of meshes) {
      m.setEnabled(false);
      m.isPickable = false;
    }
    this.templates.set(name, meshes);
  }

  _initMeshes() {
    for (const sp of MUSHROOMS) {
      const stem = MeshBuilder.CreateCylinder('fs_' + sp.id, {
        height: sp.h, diameterTop: 0.045, diameterBottom: 0.06, tessellation: 6,
      }, this.scene);
      stem.position.y = sp.h / 2;
      stem.bakeCurrentTransformIntoVertices();
      stem.material = this._mat('fsm_' + sp.id, sp.stem);
      const cap = MeshBuilder.CreateSphere('fc_' + sp.id, { diameter: 1, segments: 4 }, this.scene);
      cap.scaling.set(sp.capR * 2, sp.capR * 1.1, sp.capR * 2);
      cap.position.y = sp.h;
      cap.bakeCurrentTransformIntoVertices();
      cap.material = this._mat('fcm_' + sp.id, sp.cap);
      this._tpl(sp.id, [stem, cap]);
    }
    for (const sp of BUSHES) {
      const leaf = MeshBuilder.CreateSphere('fb_' + sp.id, { diameter: 1, segments: 4 }, this.scene);
      leaf.scaling.set(sp.size * 1.5, sp.size * 1.05, sp.size * 1.5);
      leaf.position.y = sp.size * 0.5;
      leaf.bakeCurrentTransformIntoVertices();
      leaf.material = this._mat('fbm_' + sp.id, sp.leaf);
      // berry cloud: little spheres merged into one mesh, sat ON the blob skin
      const dots = [];
      for (let k = 0; k < 14; k++) {
        const a = hash2(k, 7, 1) * Math.PI * 2, b = 0.15 + hash2(k, 11, 2) * 0.55; // upper hemisphere
        const d = MeshBuilder.CreateSphere('fbd', { diameter: Math.max(0.09, sp.size * 0.2), segments: 2 }, this.scene);
        d.position.set(
          Math.cos(a) * Math.sin(b * Math.PI) * sp.size * 0.75,
          sp.size * 0.5 + Math.cos(b * Math.PI) * sp.size * 0.5,
          Math.sin(a) * Math.sin(b * Math.PI) * sp.size * 0.75,
        );
        dots.push(d);
      }
      const berries = Mesh.MergeMeshes(dots, true, true);
      berries.name = 'fbb_' + sp.id;
      berries.material = this._mat('fbbm_' + sp.id, sp.berry);
      this._tpl(sp.id, [leaf, berries]);
    }
    // wild apple tree: trunk + crown + red dots
    {
      const trunk = MeshBuilder.CreateCylinder('fa_trunk', { height: 2.6, diameterTop: 0.22, diameterBottom: 0.34, tessellation: 6 }, this.scene);
      trunk.position.y = 1.3;
      trunk.bakeCurrentTransformIntoVertices();
      trunk.material = this._mat('fam_trunk', '#6b4a2a');
      const crown = MeshBuilder.CreateSphere('fa_crown', { diameter: 3.4, segments: 4 }, this.scene);
      crown.position.y = 3.2;
      crown.scaling.y = 0.85;
      crown.bakeCurrentTransformIntoVertices();
      crown.material = this._mat('fam_crown', '#4a7a34');
      const dots = [];
      for (let k = 0; k < 10; k++) {
        const a = hash2(k, 3, 5) * Math.PI * 2, b = hash2(k, 5, 9) * Math.PI;
        const d = MeshBuilder.CreateSphere('fad', { diameter: 0.24, segments: 2 }, this.scene);
        d.position.set(Math.cos(a) * Math.sin(b) * 1.55, 3.2 + Math.cos(b) * 1.15, Math.sin(a) * Math.sin(b) * 1.55);
        dots.push(d);
      }
      const apples = Mesh.MergeMeshes(dots, true, true);
      apples.name = 'fa_apples';
      apples.material = this._mat('fam_apples', '#c83a28');
      this._tpl('fruit_apple', [trunk, crown, apples]);
    }
    // dropped-item marker: a small canvas sack
    {
      const sack = MeshBuilder.CreateSphere('fg_sack', { diameter: 0.42, segments: 3 }, this.scene);
      sack.scaling.y = 0.7;
      sack.position.y = 0.15;
      sack.bakeCurrentTransformIntoVertices();
      sack.material = this._mat('fgm_sack', '#9a8a64');
      this._tpl('__ground', [sack]);
    }
  }

  // deterministic spots for a cell — sparse: most cells hold nothing
  cellSpots(cx, cz) {
    const out = [];
    const bx = cx * CELL, bz = cz * CELL;
    for (let i = 0; i < 3; i++) {
      if (hash2(cx, cz, 10 + i) > 0.45) continue;
      const x = bx + hash2(cx, cz, 20 + i) * CELL;
      const z = bz + hash2(cx, cz, 30 + i) * CELL;
      const biome = this.veg.biomeAt(x, z);
      const pool = MUSHROOMS.filter(s => s.biomes.includes(biome));
      if (!pool.length) continue;
      if (this.veg.maskAt(x, z)) continue;
      let tw = 0; for (const s of pool) tw += s.w;
      let r = hash2(cx, cz, 40 + i) * tw;
      const sp = pool.find(s => (r -= s.w) <= 0) || pool[0];
      const y = this.terrain.getHeight(x, z);
      if (y < 0.3) continue;
      out.push({ key: `${cx}_${cz}_m${i}`, id: sp.id, tpl: sp.id, x, y, z,
        qty: 1 + Math.floor(hash2(cx, cz, 50 + i) * 2), kind: 'mushroom' });
    }
    if (hash2(cx, cz, 60) < 0.30) {
      const x = bx + hash2(cx, cz, 61) * CELL;
      const z = bz + hash2(cx, cz, 62) * CELL;
      const biome = this.veg.biomeAt(x, z);
      const pool = BUSHES.filter(s => s.biomes.includes(biome));
      if (pool.length && !this.veg.maskAt(x, z)) {
        let tw = 0; for (const s of pool) tw += s.w;
        let r = hash2(cx, cz, 63) * tw;
        const sp = pool.find(s => (r -= s.w) <= 0) || pool[0];
        const y = this.terrain.getHeight(x, z);
        if (y > 0.3) out.push({ key: `${cx}_${cz}_b`, id: sp.id, tpl: sp.id, x, y, z,
          qty: 2 + Math.floor(hash2(cx, cz, 64) * 3), kind: 'bush' });
      }
    }
    if (hash2(cx, cz, 70) < 0.10) {
      const x = bx + hash2(cx, cz, 71) * CELL;
      const z = bz + hash2(cx, cz, 72) * CELL;
      const biome = this.veg.biomeAt(x, z);
      if ((biome === 2 || biome === 3) && !this.veg.maskAt(x, z)) {
        const y = this.terrain.getHeight(x, z);
        if (y > 0.3) out.push({ key: `${cx}_${cz}_a`, id: 'fruit_apple', tpl: 'fruit_apple', x, y, z,
          qty: 2 + Math.floor(hash2(cx, cz, 73) * 3), kind: 'tree' });
      }
    }
    return out;
  }

  rebuild(focus) {
    const spots = [];
    const c0x = Math.floor((focus.x - 260) / CELL), c1x = Math.floor((focus.x + 260) / CELL);
    const c0z = Math.floor((focus.z - 260) / CELL), c1z = Math.floor((focus.z + 260) / CELL);
    for (let cz = c0z; cz <= c1z; cz++) {
      for (let cx = c0x; cx <= c1x; cx++) {
        for (const s of this.cellSpots(cx, cz)) {
          if (!this.picked.has(s.key)) spots.push(s);
        }
      }
    }
    this.spots = spots;
    const per = new Map();
    const push = (tpl, x, y, z, rotSeed) => {
      if (!per.has(tpl)) per.set(tpl, []);
      const rot = hash2(Math.floor(x * 7), Math.floor(z * 7), rotSeed) * Math.PI * 2;
      per.get(tpl).push(Matrix.Compose(Vector3.One(), Quaternion.FromEulerAngles(0, rot, 0), new Vector3(x, y, z)));
    };
    for (const s of spots) push(s.tpl, s.x, s.y, s.z, 5);
    for (const g of this.ground) push('__ground', g.x, g.y, g.z, 6);
    for (const [name, meshes] of this.templates) {
      const mats = per.get(name);
      for (const mesh of meshes) {
        if (!mats || !mats.length) { mesh.setEnabled(false); continue; }
        mesh.setEnabled(true);
        const buf = new Float32Array(mats.length * 16);
        mats.forEach((m, i) => m.copyToArray(buf, i * 16));
        mesh.thinInstanceSetBuffer('matrix', buf, 16, false);
        mesh.thinInstanceRefreshBoundingInfo();
      }
    }
  }

  dropItem(id, qty, pos) {
    // Every spill from an equip() swap lands here, so an id the catalog does
    // not know must be refused rather than rendered: label() would read
    // ITEMS[id].name off undefined and take the interact prompt down with it.
    const n = Math.max(1, Math.floor(Number(qty) || 1));
    if (!ITEMS[id] || !pos) return null;
    const uid = (this.uidPrefix || '') + this.groundUid++;
    this.ground.push({ uid, id, qty: n, x: pos.x, y: this.terrain.getHeight(pos.x, pos.z), z: pos.z });
    this.lastBuild.set(1e9, 0, 1e9);
    this.onNet?.({ k: 'drop', uid, id, qty: n, x: pos.x, z: pos.z });
    return uid;
  }

  // drop a whole spill list (the array equip() returns) in one go
  dropAll(list, pos) {
    let n = 0;
    for (const s of list || []) if (this.dropItem(s.id, s.qty, pos)) n++;
    return n;
  }

  // ---- multiplayer sync ----
  applyPick(key) {
    this.picked.add(key);
    this.lastBuild.set(1e9, 0, 1e9);
  }

  applyDrop(g) {
    if (this.ground.some((x) => x.uid === g.uid)) return;
    this.ground.push({ uid: g.uid, id: g.id, qty: g.qty, x: g.x, y: this.terrain.getHeight(g.x, g.z), z: g.z });
    this.lastBuild.set(1e9, 0, 1e9);
  }

  applyGroundTake(uid) {
    const i = this.ground.findIndex((g) => g.uid === uid);
    if (i >= 0) { this.ground.splice(i, 1); this.lastBuild.set(1e9, 0, 1e9); }
  }


  // nearest interactable within reach: forage spot or dropped item
  nearest(pos, reach = 2.6) {
    let best = null, bestD = reach;
    for (const s of this.spots) {
      const d = Math.hypot(s.x - pos.x, s.z - pos.z);
      if (d < bestD) { best = { type: 'forage', s }; bestD = d; }
    }
    for (const g of this.ground) {
      const d = Math.hypot(g.x - pos.x, g.z - pos.z);
      if (d < bestD) { best = { type: 'ground', g }; bestD = d; }
    }
    return best;
  }

  label(target) {
    if (target.type === 'forage') {
      const s = target.s;
      const verb = s.kind === 'mushroom' ? 'Pick' : 'Gather';
      return `${verb} ${ITEMS[s.id].name}`;
    }
    const g = target.g;
    return `Take ${ITEMS[g.id].name}${g.qty > 1 ? ' ×' + g.qty : ''}`;
  }

  pick(target, inventory) {
    if (target.type === 'forage') {
      const s = target.s;
      // a spot already harvested this session (or by whoever got here first)
      // must not be pickable twice
      if (this.picked.has(s.key)) return { ok: false, msg: 'Already picked' };
      if (!inventory.addItem(s.id, s.qty)) return { ok: false, msg: 'Inventory full' };
      this.picked.add(s.key);
      this.lastBuild.set(1e9, 0, 1e9);
      this.onNet?.({ k: 'forage', key: s.key });
      return { ok: true, msg: `Picked ${ITEMS[s.id].name} ×${s.qty}` };
    }
    // Resolve against the live list before anything is removed: the old
    // `splice(indexOf(g), 1)` deleted the LAST ground drop in the world when
    // `g` was stale (A3).
    const live = findRef(this.ground, target.g, uidOf);
    if (!live) return { ok: false, msg: 'Already taken' };
    // acquire(), so a rucksack lying on the ground can be put ON rather than
    // being unpickable because nothing you wear has a hole that big (B6)
    const got = inventory.acquire(live.id, live.qty);
    if (!got.ok) return { ok: false, msg: 'Inventory full' };
    takeRef(this.ground, live, uidOf);
    this.lastBuild.set(1e9, 0, 1e9);
    this.onNet?.({ k: 'ground-take', uid: live.uid });
    const name = ITEMS[live.id].name;
    return {
      ok: true,
      msg: got.equipped ? `Put on the ${name}` : `Took ${name}${live.qty > 1 ? ' ×' + live.qty : ''}`,
    };
  }

  update(focus) {
    if (Math.hypot(focus.x - this.lastBuild.x, focus.z - this.lastBuild.z) > 120) {
      this.lastBuild.copyFrom(focus);
      this.rebuild(focus);
    }
  }
}
